import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { WxButton, WxDialog, WxLabel } from '../..';
import useCreateOrder, {
  CreateOrderInput,
} from '../../../hooks/api/mutations/useCreateOrder';
import { debitMilesCompleteErrors, debitMilesErrors } from '../../../hooks/api/queries/useOrder';
import { ProductType } from '../../../hooks/api/queries/useProduct';
import { VariantType } from '../../../hooks/api/queries/useVariantsByProductId';
import { useAddressContext } from '../../../hooks/context/useAddressContext';
import { CartItemType } from '../../../hooks/context/useCartContext';
import { useGiftContext } from '../../../hooks/context/useGiftContext';
import { __ } from '../../../i18n/l10n';
import { SLUG_CONFIRMATION } from '../../../styles/slugs';
import { appendCommitHash } from '../../../utils/appendHash';
import { splitNumber } from '../../../utils/splitNumber';
import { WImage } from '../../ui/image/Image';
import { StyledFontAwesomeIcon } from '../cart/CartItem';

interface Props {
  open: boolean;
  setOpen: (bool: boolean) => void;
  variant: VariantType;
  product: ProductType;
}

export const WxBonusModal = ({ open, setOpen, variant, product }: Props) => {
  const { mutate, isLoading } = useCreateOrder();
  const [orderFailed, setOrderFailed] = useState<boolean>(false);
  const [orderFailedMessage, setOrderFailedMessage] = useState<string>('');
  const navigate = useNavigate();
  const { giftItem, setGiftItem } = useGiftContext();

  const { accountAddress, setAddressSelected } = useAddressContext();

  useEffect(() => setAddressSelected('WINA'), [setAddressSelected]);

  const [quantity, setQuantity] = useState<number>(1);

  const price = product.priceOverwrite
    ? product.priceOverwrite.milesAmount
    : product.price.milesAmount;

  let createOrderInput: CreateOrderInput;

  useEffect(() => {
    const bonusItem: CartItemType = {
      name: product.name,
      slug: product.slug,
      quantity,
      variantId: variant?.variantId,
      price: product.priceOverwrite ? product.priceOverwrite : product.price,
      priceBefore: product.priceOverwrite ? product.price : undefined,
    };

    setGiftItem(bonusItem);
  }, [
    product.name,
    product.price,
    product.priceOverwrite,
    setGiftItem,
    variant?.variantId,
    quantity,
    product.slug,
  ]);

  if (giftItem?.variantId && giftItem?.quantity)
    createOrderInput = {
      address: accountAddress,
      totalMilesAmount: giftItem.price.milesAmount * giftItem.quantity,
      items: [giftItem],
    };

  const image = product.defaultImages?.[0];

  return (
    <>
      {orderFailed && (
        <WxDialog
          title={__('Unable to order')}
          content={debitMilesErrors[orderFailedMessage]
            ? debitMilesCompleteErrors[orderFailedMessage] ? debitMilesCompleteErrors[orderFailedMessage] : __('Your order could not be completed because of %1$s', [
                debitMilesErrors[orderFailedMessage],
              ])
            : __('Your order could not be completed')}
          open={orderFailed}
          setOpen={setOrderFailed}
          onClose={() => navigate(appendCommitHash(`/`))}
          noCancel
        />
      )}
      <DivOnTop open={open}>
        <div
          style={{
            backgroundColor: 'black',
            borderColor: 'rgba(255,255,255, 0.2)',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        >
          <TopFlex>
            <div>
              <WImage image={image} />
            </div>

            <Flex>
              <WxLabel label={product.name} />
            </Flex>
          </TopFlex>

          <div>
            <QtyFlex>
              <StyledFontAwesomeIcon
                icon={faMinus}
                onClick={() => setQuantity((qty) => qty - 1)}
                disabled={quantity === 1}
              />
              {quantity}
              <StyledFontAwesomeIcon
                icon={faPlus}
                onClick={() => setQuantity((qty) => qty + 1)}
              />
            </QtyFlex>
          </div>

          <div>
            <Flex>
              <WxLabel label="Total" />
              <WxLabel
                label={`${splitNumber(price * quantity)} ${__('MILES')}`}
              />
            </Flex>

            <ButtonContainer>
              <WxButton
                label={__('Cancel')}
                onClick={() => {
                  setOpen(false);
                }}
                darkMode
                type={'neutral'}
                expand
                noArrow
              />
              <WxButton
                label={__('Confirm')}
                onClick={() => {
                  if (!isLoading)
                    mutate(createOrderInput, {
                      onSuccess: ({ orderId }) => {
                        navigate(appendCommitHash(`/${SLUG_CONFIRMATION}`), {
                          state: { orderId },
                        });
                      },
                      onError: (params) => {
                        setOpen(false);
                        setOrderFailedMessage(params.message);
                        setOrderFailed(true);
                      },
                    });
                }}
                type={'validation'}
                disabled={isLoading}
                expand
              />
            </ButtonContainer>
          </div>
        </div>
      </DivOnTop>
    </>
  );
};

const ButtonContainer = styled.div`
  width: 100%;
  gap: 0.625rem;
  margin-top: 0.625rem;
  display: flex;
  justify-content: space-between;
`;

const DivOnTop = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 4;
  background-color: hsla(0, 0%, 0%, 0.8);
  width: 120%;
  height: 100vh;
  margin-left: -10%;
  display: ${({ open }) => (open ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  > div {
    width: 18.125rem;
    padding: 1.25rem;

    > * {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0.625rem;
`;

const TopFlex = styled.div`
  display: flex;
  gap: 0.625rem;
`;

const QtyFlex = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.white};
  padding-inline: 0.9575rem;
  padding-block: 0.625rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  width: 100px;
  margin-bottom: 20px;
`;
